import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useAxiosAuthInterceptor } from "./hooks/useAxiosAuthInterceptor";

import Footer from "./components/Footer/Footer";
import DataModel from "./pages/Vocabularies/DataModel";
import ResourceDetail from "./pages/Vocabularies/Crud/ResourceDetail";
import CreateResource from "./pages/Vocabularies/Crud/CreateResource";
import EditResource from "./pages/Vocabularies/Crud/EditResource";
import DataOffering from "./pages/DataOfferings/DataOfferings";
import DataOfferingDetail from "./pages/DataOfferings/Crud/OfferingDetail";
import CreateDataOffering from "./pages/DataOfferings/Crud/CreateOffering";
import EditOffering from "./pages/DataOfferings/Crud/EditOffering";
import UserPage from "./pages/UserProfile/UserPage";
import ActionLog from "./pages/PrivateArea/ActionLog";
import ActivityTracking from "./pages/AdminDSB/ActivityTracking";
import UserControl from "./pages/AdminDSB/UserControl";
import CreateUser from "./pages/AdminDSB/CreateUser";
import MyDatasets from "./pages/PrivateArea/Mydatasets";
import MyContracts from "./pages/PrivateArea/Contracts/MyContracts";
import PublicWebsite from "./pages/PublicWebsite/PublicWebsite";
import AppLayout from "./components/Layout/AppLayout";
import ParticipantManagement from "./pages/AdminDSB/ParticipantManagement";
import ParticipantDiscovery from "./pages/ParticipantDiscovery/ParticipantDiscovery";
import CreateParticipant from "./pages/AdminDSB/ParticipantDetails/CreateParticipant";
import EditParticipant from "./pages/AdminDSB/ParticipantDetails/EditParticipant";
import RegisterControl from "./pages/AdminDSB/RegisterControl";

import { UserProvider } from "./hooks/UserContext";
import { RoleBasedRoute } from "./components/RoleExtraction";
import Unauthorized from "./pages/PrivateArea/Unauthorized";
import SessionExpired from "./pages/Auth/SessionExpired";

import i18n from "./i18n";
import LandingPage from "./pages/Home/LandingPage";
import { Spinner } from "./components/Filtering/Reusable/Spinner";

import { getSubdomainAndDomain } from "./utils/constants";
import "react-toastify/dist/ReactToastify.css";
import { appConfig } from "./utils/config";
import { t } from "i18next";
import { ProtectedRoute } from "./hooks/ProtectedRoute";
import WizardCreation from "./components/Wizard/WizardCreation";
import AppProviders from "./AppProviders";
import MyPolicies from "./pages/PrivateArea/Policies/MyPolicies";
import MyDataService from "./pages/PrivateArea/DataService/MyDataService";

function AppContent({ sidebarOpen, toggleSidebar, handleCloseSideBar }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const isInitialAuthLoading = isLoading && !isAuthenticated;

  useEffect(() => {
    const savedLng = localStorage.getItem("appLng");
    document.title = t(`clients.${appConfig.client}.title`);
    if (savedLng) {
      i18n.changeLanguage(savedLng).catch((err) => {
        console.error(err);
      });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      window.token = user?.id_token || user?.access_token;
    }
  }, [isAuthenticated, user]);

  if (isInitialAuthLoading) {
    return (
      <div className="spinner-container">
        <Spinner size="large" color="var(--main-color)" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public route */}
      <Route
        path="/"
        element={
          <div className="App">
            <main className="app-content">
              <PublicWebsite />
            </main>
            <Footer />
          </div>
        }
      />
      <Route path="/session-expired" element={<SessionExpired />} />
      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppLayout
              sidebarOpen={sidebarOpen}
              toggleSidebar={toggleSidebar}
              handleCloseSideBar={handleCloseSideBar}
            >
              <Routes>
                {/* Rutas sin protección específica */}
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/home" element={<LandingPage />} />
                <Route path="/datamodel" element={<DataModel />} />
                <Route path="/actionlog" element={<ActionLog />} />
                <Route path="/mycontracts" element={<MyContracts />} />
                <Route path="/dataservice" element={<MyDataService />} />
                <Route path="/policies" element={<MyPolicies />} />
                {/* <Route path="/createpolicies" element={<CreatePolicy />} />
                <Route
                  path="/createdataservice"
                  element={<CreateDataService />}
                /> */}

                <Route path="/mydatasets" element={<MyDatasets />} />
                <Route
                  path="/resourcedetail/:identifier"
                  element={<ResourceDetail />}
                />
                <Route path="/dataoffering" element={<DataOffering />} />
                <Route
                  path="/dataofferingdetail/:id"
                  element={<DataOfferingDetail />}
                />
                <Route
                  path="/participantdiscovery"
                  element={<ParticipantDiscovery />}
                />
                <Route path="/profilepage" element={<UserPage />} />
                {/* Rutas con protección específica */}
                <Route
                  path="/activitytracking"
                  element={
                    <RoleBasedRoute allowedRoles={["admin"]} adminOnly>
                      <ActivityTracking />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/usercontrol"
                  element={
                    <RoleBasedRoute allowedRoles={["admin"]} adminOnly>
                      <UserControl />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createuser"
                  element={
                    <RoleBasedRoute allowedRoles={["admin"]} adminOnly>
                      <CreateUser />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/registerequests"
                  element={
                    <RoleBasedRoute allowedRoles={["admin"]} adminOnly>
                      <RegisterControl />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/participantmanagement"
                  element={
                    <RoleBasedRoute allowedRoles={["admin"]} adminOnly>
                      <ParticipantManagement />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/offeringdata"
                  element={
                    <RoleBasedRoute allowedRoles={["data_provider"]}>
                      <WizardCreation />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createparticipant"
                  element={
                    <RoleBasedRoute allowedRoles={["data_provider"]}>
                      <CreateParticipant />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/editparticipant/:id"
                  element={
                    <RoleBasedRoute allowedRoles={["data_provider"]}>
                      <EditParticipant />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createresource"
                  element={
                    <RoleBasedRoute allowedRoles={["data_provider"]}>
                      <CreateResource />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/editresource/:identifier"
                  element={
                    <RoleBasedRoute allowedRoles={["data_provider"]}>
                      <EditResource />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createdataoffering"
                  element={
                    <RoleBasedRoute allowedRoles={["data_provider"]}>
                      <CreateDataOffering />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/editdataoffering/:identifier"
                  element={
                    <RoleBasedRoute allowedRoles={["data_provider"]}>
                      <EditOffering />
                    </RoleBasedRoute>
                  }
                />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const handleCloseSideBar = () => setSidebarOpen(false);

  const { domain } = getSubdomainAndDomain();
  const isProd = domain === "nttdata-dataspacebuilder.com";
  const base = isProd ? "" : "/";

  useAxiosAuthInterceptor();

  return (
    <UserProvider>
      <AppProviders>
        <BrowserRouter basename={base}>
          <AppContent
            sidebarOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
            handleCloseSideBar={handleCloseSideBar}
          />
        </BrowserRouter>
      </AppProviders>
    </UserProvider>
  );
}
